import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Card,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/env';

// Anon client — no auth required for public schedules
const anonSupabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

type PublicEntry = {
  entry_id: string;
  entry_date: string;
  start_time: string;
  end_time: string;
  title: string;
  description: string | null;
  location: string | null;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  category_icon: string | null;
  form_title: string;
  event_start_date: string | null;
  event_end_date: string | null;
};

function strip(t: string) { return t.slice(0, 5); }

function fmtDate(d: string) {
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
}

function hexRgba(hex: string, a: number) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function groupByDate(entries: PublicEntry[]): [string, PublicEntry[]][] {
  const map = new Map<string, PublicEntry[]>();
  for (const e of entries) {
    const list = map.get(e.entry_date) ?? [];
    list.push(e);
    map.set(e.entry_date, list);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function PublicSchedulePage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const { data: entries, isLoading, error } = useQuery<PublicEntry[]>({
    queryKey: ['public-schedule', slug],
    queryFn: async () => {
      const { data, error } = await anonSupabase.rpc('get_public_schedule', {
        p_form_slug: slug,
      });
      if (error) throw error;
      return (data ?? []) as PublicEntry[];
    },
    enabled: !!slug,
  });

  const grouped = useMemo(() => groupByDate(entries ?? []), [entries]);
  const meta = entries?.[0];
  const today = new Date().toISOString().slice(0, 10);

  // Auto-select today or first available day
  const activeDays = grouped.map(([d]) => d);
  const effectiveDate = selectedDate && activeDays.includes(selectedDate)
    ? selectedDate
    : activeDays.includes(today)
      ? today
      : activeDays[0] ?? null;

  const dayEntries = grouped.find(([d]) => d === effectiveDate)?.[1] ?? [];

  if (isLoading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loader />
      </Box>
    );
  }

  if (error || !entries?.length) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: 12 }}>
        <Text size="xl">📅</Text>
        <Title order={3}>Kein Ablaufplan verfügbar</Title>
        <Text c="dimmed" size="sm">
          Dieser Ablaufplan wurde nicht gefunden oder ist noch nicht veröffentlicht.
        </Text>
      </Box>
    );
  }

  return (
    <Box style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px', minHeight: '100vh' }}>
      {/* Header */}
      <Stack gap={4} mb="lg">
        <Title order={2}>{meta?.form_title ?? 'Ablaufplan'}</Title>
        {meta?.event_start_date && meta?.event_end_date && (
          <Text c="dimmed" size="sm">
            {fmtDate(meta.event_start_date)} – {fmtDate(meta.event_end_date)}
          </Text>
        )}
        <Text size="xs" c="dimmed">Nur zur Ansicht · keine Anmeldung erforderlich</Text>
      </Stack>

      <Divider mb="md" />

      {/* Day tabs */}
      {activeDays.length > 1 && (
        <ScrollArea type="scroll" scrollbarSize={4} mb="md">
          <Group gap={8} wrap="nowrap" pb={4}>
            {grouped.map(([date], i) => {
              const isActive = date === effectiveDate;
              const isToday = date === today;
              return (
                <UnstyledButton
                  key={date}
                  onClick={() => setSelectedDate(date)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 20,
                    background: isActive ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-default)',
                    color: isActive ? '#fff' : 'var(--mantine-color-text)',
                    fontWeight: isActive ? 700 : 400,
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                    border: isToday && !isActive ? '1px solid var(--mantine-color-blue-4)' : '1px solid transparent',
                    flexShrink: 0,
                  }}
                >
                  Tag {i + 1}{isToday ? ' · Heute' : ''}
                </UnstyledButton>
              );
            })}
          </Group>
        </ScrollArea>
      )}

      {/* Date heading */}
      {effectiveDate && (
        <Text fw={700} mb="sm" size="sm" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
          {fmtDate(effectiveDate)}
        </Text>
      )}

      {/* Entries */}
      <Stack gap="sm">
        {dayEntries.map((entry) => {
          const color = entry.category_color ?? '#6366F1';
          const now = new Date();
          const [sh, sm2] = entry.start_time.split(':').map(Number);
          const [eh, em] = entry.end_time.split(':').map(Number);
          const entryDate = new Date(entry.entry_date + 'T00:00:00');
          const isToday2 = entryDate.toDateString() === now.toDateString();
          const nowMins = now.getHours() * 60 + now.getMinutes();
          const startMins = sh * 60 + sm2;
          const endMins = eh * 60 + em;
          const isNow = isToday2 && nowMins >= startMins && nowMins < endMins;

          return (
            <Card
              key={entry.entry_id}
              radius="md"
              p="sm"
              withBorder
              style={{
                borderLeft: `4px solid ${color}`,
                background: isNow ? hexRgba(color, 0.06) : undefined,
                outline: isNow ? `2px solid ${color}` : undefined,
                outlineOffset: 1,
              }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Group align="flex-start" gap="sm" wrap="nowrap">
                  {/* Time */}
                  <Stack gap={0} style={{ minWidth: 52, flexShrink: 0 }}>
                    <Text size="sm" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {strip(entry.start_time)}
                    </Text>
                    <Text size="xs" c="dimmed">– {strip(entry.end_time)}</Text>
                  </Stack>

                  {/* Content */}
                  <Stack gap={4}>
                    {entry.category_name && (
                      <Badge
                        size="xs"
                        color={color}
                        variant="filled"
                        style={{ alignSelf: 'flex-start' }}
                      >
                        {entry.category_icon ? `${entry.category_icon} ` : ''}{entry.category_name}
                      </Badge>
                    )}
                    <Text size="sm" fw={600}>{entry.title}</Text>
                    {entry.location && (
                      <Text size="xs" c="dimmed">📍 {entry.location}</Text>
                    )}
                    {entry.description && (
                      <Text size="xs" c="dimmed">{entry.description}</Text>
                    )}
                  </Stack>
                </Group>

                {isNow && (
                  <Badge color="green" size="xs" variant="dot" style={{ flexShrink: 0 }}>
                    Jetzt
                  </Badge>
                )}
              </Group>
            </Card>
          );
        })}
      </Stack>

      <Text size="xs" c="dimmed" ta="center" mt="xl">
        MusikalKultur · Ablaufplan
      </Text>
    </Box>
  );
}
